import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { Subject, fromEvent, takeUntil } from 'rxjs';
import * as THREE from 'three';

import {
  ProfileFakeOctree,
  type ProfilePointBatch,
  countProfilePoints,
  syncProfileMaterial,
} from './profile-potree-adapter';
import { ProfileWindowService } from './profile-window.service';
import { ProfileWindowControllerService } from '../services/profile-window-controller.service';
import { createPointCloudRenderer, type PotreeRenderer } from '../services/pointcloud-runtime.js';
import { ToolsService } from '../services/tools.service';
import { ViewerCoreService } from '../services/viewer-core.service';
import { ButtonComponent } from '../ui/button/button.component';

export interface ProfileSelection {
  position: THREE.Vector3;
  rgb: [number, number, number];
  elevation: number;
  mileage: number;
}

@Component({
  selector: 'app-profile-window',
  imports: [CommonModule, ButtonComponent],
  templateUrl: './profile-window.component.html',
  styleUrls: ['./profile-window.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileWindowComponent implements AfterViewInit, OnDestroy {
  @ViewChild('profileCanvasContainer', { static: false })
  canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('profileWindow', { static: false })
  profileWindowElement!: ElementRef<HTMLDivElement>;
  @Input() viewer: unknown;
  @Input() visible = false;
  @Output() visibilityChanged = new EventEmitter<boolean>();
  @Output() profileSelection = new EventEmitter<ProfileSelection | null>();
  private profileService = inject(ProfileWindowService);
  private tools = inject(ToolsService);
  private core = inject(ViewerCoreService);
  private profileCtrl = inject(ProfileWindowControllerService);
  private destroy$ = new Subject<void>();
  renderer!: THREE.WebGLRenderer;
  profileScene!: THREE.Scene;
  scene!: THREE.Scene;
  camera!: THREE.OrthographicCamera;
  pickSphere!: THREE.Mesh;
  pRenderer: PotreeRenderer | undefined;
  private autoFitDebounce?: ReturnType<typeof setTimeout>;
  mouseIsDown = false;
  mouse = new THREE.Vector2();
  scale = new THREE.Vector3(1, 1, 1);
  projectedBox = new THREE.Box3();
  pointclouds = new Map<unknown, ProfileFakeOctree>();
  numPoints = signal(0);
  autoFitEnabled = true;
  autoFit = true;
  enabled = false;
  selectedPosition = signal<THREE.Vector3 | undefined>(undefined);
  selectedRgb = signal<[number, number, number] | undefined>(undefined);
  showSelectionProperties = signal(false);
  rotateAmount = signal(10);

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private windowStartX = 0;
  private windowStartY = 0;
  private currentOffsetX = 0;
  private currentOffsetY = 0;

  ngAfterViewInit(): void {
    this.initTHREE();
    this.initListeners();
    this.initDragListeners();
    this.connectToProfileController();
    this.enabled = true;
    // Initialize camera bounds
    this.updateCameraBounds();
    this.render();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanup();
  }
  private initTHREE(): void {
    this.renderer = new THREE.WebGLRenderer({ alpha: true, premultipliedAlpha: false });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.autoClear = false;
    this.renderer.setSize(32, 32);
    this.canvasContainer.nativeElement.appendChild(this.renderer.domElement);
    this.camera = new THREE.OrthographicCamera(-1000, 1000, 1000, -1000, -1000, 1000);
    this.camera.up.set(0, 0, 1);
    this.camera.rotation.order = 'ZXY';
    this.camera.rotation.x = Math.PI / 2;
    this.profileScene = new THREE.Scene();
    this.scene = new THREE.Scene();
    this.pickSphere = new THREE.Mesh(
      new THREE.SphereGeometry(1, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0xffff00 }),
    );
    this.pickSphere.visible = false;
    this.scene.add(this.pickSphere);
    try {
      this.pRenderer = createPointCloudRenderer(this.renderer);
    } catch {
      console.warn(
        'Potree point cloud renderer not available, profile window will not render point clouds.',
      );
    }
  }
  private initListeners(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.enabled) this.render();
      });
    fromEvent(this.canvasContainer.nativeElement, 'mousedown')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.mouseIsDown = true;
      });
    fromEvent(this.canvasContainer.nativeElement, 'mouseup')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.mouseIsDown = false;
      });
    fromEvent(this.canvasContainer.nativeElement, 'mousemove')
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => this.handleMouseMove(e as MouseEvent));
    fromEvent(this.canvasContainer.nativeElement, 'wheel')
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => this.handleWheel(e as WheelEvent));
  }

  private initDragListeners(): void {
    // Use setTimeout to ensure ViewChild is available
    setTimeout(() => {
      if (!this.profileWindowElement) return;

      const titlebar = this.profileWindowElement.nativeElement.querySelector('.profile-titlebar');
      if (!titlebar) return;

      fromEvent<MouseEvent>(titlebar, 'mousedown')
        .pipe(takeUntil(this.destroy$))
        .subscribe(e => {
          // Only drag if clicking on titlebar, not close button
          if ((e.target as HTMLElement).closest('.close-button')) return;

          this.isDragging = true;
          this.dragStartX = e.clientX;
          this.dragStartY = e.clientY;

          // Store the current offset at the start of drag
          this.windowStartX = this.currentOffsetX;
          this.windowStartY = this.currentOffsetY;

          e.preventDefault();
        });

      fromEvent<MouseEvent>(document, 'mousemove')
        .pipe(takeUntil(this.destroy$))
        .subscribe(e => {
          if (!this.isDragging) return;

          const deltaX = e.clientX - this.dragStartX;
          const deltaY = e.clientY - this.dragStartY;

          this.currentOffsetX = this.windowStartX + deltaX;
          this.currentOffsetY = this.windowStartY + deltaY;

          this.profileWindowElement.nativeElement.style.transform = `translate(calc(-50% + ${this.currentOffsetX}px), calc(-50% + ${this.currentOffsetY}px))`;

          e.preventDefault();
        });

      fromEvent<MouseEvent>(document, 'mouseup')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.isDragging = false;
        });
    }, 0);
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.pointclouds.size === 0) return;
    const r = this.canvasContainer.nativeElement.getBoundingClientRect();
    const np = new THREE.Vector2(e.clientX - r.left, e.clientY - r.top);
    if (this.mouseIsDown) {
      const dx = np.x - this.mouse.x;
      const dy = np.y - this.mouse.y;
      this.camera.position.x -= dx / this.scale.x;
      this.camera.position.z += dy / this.scale.y;
      this.render();
    }
    this.mouse.copy(np);
  }
  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    this.autoFit = false;
    const dir = Math.sign(e.deltaY);
    this.scale.multiplyScalar(dir > 0 ? 0.9 : 1.1);
    this.render();
  }
  addPoints(pointcloud: unknown, points: unknown): void {
    if (!this.enabled) return;
    const meta = points as ProfilePointBatch;
    if (!meta || meta.numPoints === 0) return;
    let oct = this.pointclouds.get(pointcloud);
    if (!oct) {
      oct = new ProfileFakeOctree(pointcloud);
      this.pointclouds.set(pointcloud, oct);
      this.profileScene.add(oct as unknown as THREE.Object3D);
      const pcMat = (pointcloud as any).material;
      if (pcMat?.addEventListener)
        pcMat.addEventListener('material_property_changed', () =>
          requestAnimationFrame(() => this.render()),
        );
    }
    oct.addPoints(meta);
    if (oct.projectedBox && !oct.projectedBox.isEmpty()) this.projectedBox.union(oct.projectedBox);
    if (this.autoFit && this.autoFitEnabled) this.scheduleAutoFit();
    this.updatePointCount();
    this.render();
  }
  private updatePointCount(): void {
    this.numPoints.set(countProfilePoints(this.pointclouds.values()));
  }
  update(): void {
    if (this.autoFit && this.autoFitEnabled) this.scheduleAutoFit();
    this.updatePointCount();
    this.render();
  }
  reset(): void {
    this.projectedBox.makeEmpty();
    for (const o of this.pointclouds.values()) {
      o.dispose();
      this.profileScene.remove(o as unknown as THREE.Object3D);
    }
    this.pointclouds.clear();
    this.numPoints.set(0);
    this.autoFit = true;
    this.scale.set(1, 1, 1);
    this.pickSphere.visible = false;
    // Reset camera position to default
    this.camera.position.set(0, 0, 0);
    this.updateCameraBounds();
    this.render();
  }
  private scheduleAutoFit(): void {
    if (this.autoFitDebounce) clearTimeout(this.autoFitDebounce);
    this.autoFitDebounce = setTimeout(() => {
      this.updateCameraToFitPoints();
      this.render();
    }, 1000);
  }
  private updateCameraToFitPoints(): void {
    if (this.pointclouds.size === 0) return;

    // Calculate the overall projected box from all point clouds
    const box = new THREE.Box3();
    for (const o of this.pointclouds.values()) {
      if (o.projectedBox && !o.projectedBox.isEmpty()) {
        box.union(o.projectedBox);
      }
    }
    if (box.isEmpty()) return;

    const width = this.canvasContainer.nativeElement.clientWidth;
    const height = this.canvasContainer.nativeElement.clientHeight;
    const size = box.getSize(new THREE.Vector3());

    // Calculate scale to fit points in viewport (like original implementation)
    const sx = width / size.x;
    const sy = height / size.z;
    const scale = Math.min(sx, sy);

    // Set the scale and position camera at center of projected box
    const center = box.getCenter(new THREE.Vector3());
    this.scale.set(scale, scale, 1);
    this.camera.position.copy(center);

    // Update camera bounds based on scale and viewport size (like original updateScales)
    this.updateCameraBounds();
  }

  private updateCameraBounds(): void {
    const width = this.canvasContainer.nativeElement.clientWidth;
    const height = this.canvasContainer.nativeElement.clientHeight;

    // Calculate camera bounds relative to scale and viewport size (from original updateScales)
    const left = -width / 2 / this.scale.x;
    const right = +width / 2 / this.scale.x;
    const top = +height / 2 / this.scale.y;
    const bottom = -height / 2 / this.scale.y;

    this.camera.left = left;
    this.camera.right = right;
    this.camera.top = top;
    this.camera.bottom = bottom;
    this.camera.updateProjectionMatrix();
  }
  show(): void {
    this.visible = true;
    this.enabled = true;
    this.visibilityChanged.emit(true);
  }
  hide(): void {
    this.visible = false;
    this.enabled = false;
    this.visibilityChanged.emit(false);
  }
  render(): void {
    const w = this.canvasContainer.nativeElement.clientWidth;
    const h = this.canvasContainer.nativeElement.clientHeight;
    this.renderer.setSize(w, h);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, false);

    this.updateCameraBounds();

    this.camera.updateMatrixWorld(false);
    this.camera.updateProjectionMatrix();
    for (const [pc, fake] of this.pointclouds.entries()) {
      syncProfileMaterial((pc as { material?: unknown }).material, fake);
    }
    this.pRenderer?.render(this.profileScene, this.camera, null);
    this.renderer.render(this.scene, this.camera);
  }
  private cleanup(): void {
    if (this.renderer) this.renderer.dispose();
    for (const o of this.pointclouds.values()) o.dispose();
    this.pointclouds.clear();
  }
  private connectToProfileController(): void {
    this.tools.setAngularProfileWindow(this);
    this.profileCtrl.profileWindow = this;
    if (this.profileCtrl.profile) {
      this.reset();
      this.profileCtrl.recompute();
    }
  }
  onRotateAmountChange(value: number): void {
    if (!isNaN(value) && value >= 1 && value <= 90) {
      this.rotateAmount.set(value);
    }
  }

  onRotateClockwise(): void {
    const radians = THREE.MathUtils.degToRad(this.rotateAmount());
    this.rotateProfile(-radians);
  }

  onRotateCounterClockwise(): void {
    const radians = THREE.MathUtils.degToRad(this.rotateAmount());
    this.rotateProfile(radians);
  }

  onMoveForward(): void {
    this.moveProfile(1);
  }

  onMoveBackward(): void {
    this.moveProfile(-1);
  }

  private rotateProfile(radians: number): void {
    const profile = this.getActiveProfile();
    if (!profile || !profile.points || profile.points.length < 2) return;

    const points = profile.points;
    const start = points[0];
    const end = points[points.length - 1];
    const center = start.clone().add(end).multiplyScalar(0.5);

    // Create transformation matrices
    const mMoveOrigin = new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z);
    const mRotate = new THREE.Matrix4().makeRotationZ(radians);
    const mMoveBack = new THREE.Matrix4().makeTranslation(center.x, center.y, center.z);
    const transform = mMoveBack.multiply(mRotate).multiply(mMoveOrigin);

    // Apply transformation to all points
    const rotatedPoints = points.map((point: THREE.Vector3) =>
      point.clone().applyMatrix4(transform),
    );

    for (let i = 0; i < points.length; i++) {
      if (typeof profile.setPosition === 'function') {
        profile.setPosition(i, rotatedPoints[i]);
      }
    }

    // Re-enable autofit after profile geometry changes so camera follows the new position
    this.autoFit = true;
    this.autoFitEnabled = true;
  }

  private moveProfile(direction: number): void {
    const profile = this.getActiveProfile();
    if (!profile || !profile.points || profile.points.length < 2) return;

    const points = profile.points;
    const start = points[0];
    const end = points[points.length - 1];

    // Calculate direction vectors
    const dir = end.clone().sub(start).normalize();
    const up = new THREE.Vector3(0, 0, 1);
    const forward = up.cross(dir);
    const move = forward.clone().multiplyScalar((profile.width / 2) * direction);

    for (let i = 0; i < points.length; i++) {
      if (typeof profile.setPosition === 'function') {
        profile.setPosition(i, points[i].clone().add(move));
      }
    }

    // Re-enable autofit after profile geometry changes so camera follows the new position
    this.autoFit = true;
    this.autoFitEnabled = true;
  }

  private getActiveProfile(): any {
    return this.profileCtrl.profile || null;
  }
  onDownloadDXF2D(): void {
    const dxf = this.profileService.exportToDXF2D(this.pointclouds);
    this.download(dxf, 'profile_2d.dxf', 'application/dxf');
  }
  onDownloadDXF3D(): void {
    const dxf = this.profileService.exportToDXF3D(this.pointclouds);
    this.download(dxf, 'profile_3d.dxf', 'application/dxf');
  }
  onDownloadCSV(): void {
    const csv = this.profileService.exportToCSV(this.pointclouds);
    this.download(csv, 'profile.csv', 'text/csv');
  }
  onDownloadLAS(): void {
    const las = this.profileService.exportToLAS(this.pointclouds);
    this.downloadBinary(las, 'profile.las', 'application/octet-stream');
  }
  onClose(): void {
    this.hide();
  }
  private download(content: string, filename: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private downloadBinary(buffer: ArrayBuffer, filename: string, mime: string): void {
    const blob = new Blob([buffer], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
